import {
  producing,
  planned,
  unplanned,
  waiting,
  uncollected,
  untagged,
  operation,
  darkGreen,
  speedLoss,
} from "../../../../constants/colors";
import {
  CategoryPropsType,
  PnlCategoriesEnum,
  PnlCategoryLabelsEnum,
  ProducingLossesEnum,
  ProducingLossesLabelsEnum,
} from "../../../../types";
import { PnlTimes } from "./pnlChartTypes";
import { round } from "lodash";
import { GENERIC_PNL_CATEGORY_PROPS_ARR } from "../../../../constants/customers";
import { PnlBreakdown } from "../../../../getters/pnlBreakdownGetter/pnlBreakdownTypes";

/**
 * Returns list of PnlTimes that are < or >= constraint
 *
 * @param {({
 *   pnlSubTagArray: PnlTimes[] | undefined;
 *   constraint?: number;
 *   shouldBeLessThan: boolean;
 * })} {
 *   pnlSubTagArray,
 *   constraint = 1,
 *   shouldBeLessThan
 * }
 * @return {*}  {(PnlTimes[] | undefined)}
 */
export const filterByPercentValue = ({
  pnlSubTagArray,
  constraint = 1,
  shouldBeLessThan,
}: {
  pnlSubTagArray?: PnlTimes[];
  constraint?: number;
  shouldBeLessThan: boolean;
}): PnlTimes[] | undefined => {
  return shouldBeLessThan
    ? pnlSubTagArray &&
        pnlSubTagArray.filter(({ value }) => value && value < constraint)
    : pnlSubTagArray &&
        pnlSubTagArray.filter(({ value }) => value && value >= constraint);
};

/**
 * Takes categoryPropsArr and transforms it into PnlTimes[] for PnlChart
 *
 * @param {{
 *   data: DataDumpAggregateType;
 *   categoryPropsArr: CategoryPropsType[];
 *   nbEvents?: any;
 *   lostOutput?:any;
 *   subTagsToHide?: string[];
 * }} {
 *   data,
 *   categoryPropsArr,
 *   nbEvents,
 *   lostOutput,
 *   subTagsToHide
 * }
 * @return {*}  {PnlTimes[]}
 */
export const getPnlTimes = ({
  data,
  categoryPropsArr = GENERIC_PNL_CATEGORY_PROPS_ARR(),
  subTagsToHide,
  subTagsToExclude,
}: {
  data: PnlBreakdown;
  categoryPropsArr?: CategoryPropsType[];
  nbEvents?: any;
  lostOutput?: any;
  subTagsToHide?: string[];
  subTagsToExclude?: string[];
}): PnlTimes[] => {
  const MAP_CATEGORY_TO_COLOR_AND_TIME = {
    [PnlCategoriesEnum.producing]: {
      color: producing,
      time: data.producing.durationMs,
    },
    [PnlCategoriesEnum.planned]: {
      color: planned,
      time: data.planned.durationMs,
    },
    [PnlCategoriesEnum.unplanned]: {
      color: unplanned,
      time: data.unplanned.durationMs,
    },
    [PnlCategoriesEnum.waiting]: {
      color: waiting,
      time: data.waiting.durationMs,
    },
    [PnlCategoriesEnum.unknown]: {
      color: untagged,
      time: data.unknown.durationMs,
    },
    [PnlCategoriesEnum.notScheduled]: {
      color: operation,
      time: data.notScheduled.durationMs,
    },
    [PnlCategoriesEnum.uncollected]: {
      color: uncollected,
      time: data.uncollected.durationMs,
    },
  };

  const toPercent = (num: number): number => {
    const percent = data.productionTime ? 100 * (num / data.productionTime) : 0;
    return round(percent, Math.abs(percent) < 1 ? 1 : 0);
  };

  const toWeightedPercent = (num: number): number => {
    const maxTimeToCalculateFrom = Math.max(
      ...[
        data.unplanned.durationMs,
        data.waiting.durationMs,
        data.unknown.durationMs,
        data.producing.durationMs,
        data.planned.durationMs,
      ]
    );
    const percent = 100 * (num / maxTimeToCalculateFrom);
    return round(percent, Math.abs(percent) < 1 ? 1 : 0);
  };

  const getPnlCategoryProps = ({
    category,
    name,
    title,
    shouldWeightBars = false,
    shouldHideValue = false,
    hidePercentBar = false,
    shouldFillWithLines = false,
    hideBorder = false,
    hideNbEvents = false,
    hideLostOutput = false,
    specialSubTags,
  }: CategoryPropsType) => {
    const fields = MAP_CATEGORY_TO_COLOR_AND_TIME[category];
    const categoryName = name ? name : category;

    const commonTimeProps = (time: number) => ({
      value: toPercent(time),
      weightedValue: shouldWeightBars ? toWeightedPercent(time) : undefined,
    });

    const specialSubTagProps = specialSubTags?.map(subTag => ({
      ...subTag,
      nbEvents: subTag.nbEvents && !hideNbEvents ? subTag.nbEvents : "--",
      lostOutput: subTag.lostOutput ? subTag.lostOutput : "",
      ...commonTimeProps(subTag.time),
      color: subTag.color || fields.color,
    }));

    const subTags = data?.[categoryName]?.tags
      ?.map(
        ({
          tagName,
          durationMs: time,
          numberOfEvents: nbEvents,
          averageDurationMs: avgDuration,
          targetOutput: lostOutput,
        }) =>
          subTagsToExclude?.includes(tagName)
            ? ""
            : {
                ...commonTimeProps(time),
                title: tagName,
                name: tagName,
                time,
                nbEvents: hideNbEvents ? "--" : nbEvents,
                avgDuration,
                lostOutput: hideLostOutput ? "" : Math.round(lostOutput),
                color: fields.color,
                shouldHideValue,
                shouldFillWithLines,
                hideBorder,
                hidePercentBar: subTagsToHide?.includes(categoryName),
              }
      )
      .sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0));

    return {
      ...fields,
      ...commonTimeProps(fields.time),
      title: title || PnlCategoryLabelsEnum[categoryName] || name,
      name: categoryName,
      nbEvents: hideNbEvents ? "--" : data?.[categoryName].numberOfEvents,
      avgDuration: data?.[categoryName].averageDurationMs,
      lostOutput: hideLostOutput
        ? ""
        : Math.round(data?.[categoryName].targetOutput),
      shouldHideValue,
      hidePercentBar,
      shouldFillWithLines,
      hideBorder,
      subTags: specialSubTags ? specialSubTagProps : subTags,
    };
  };

  return categoryPropsArr.map(categoryProps => {
    return { ...getPnlCategoryProps({ ...categoryProps }) };
  });
};

export const EFFECTIVE_PRODUCING = (data: PnlBreakdown) => ({
  title: ProducingLossesLabelsEnum.effectiveProducingTime,
  name: ProducingLossesEnum.effectiveProducingTime,
  time: data.producing.effectiveProducing.durationMs,
  nbEvents: data.producing.effectiveProducing.numberOfEvents,
});

export const QUALITY_LOSS = (data: PnlBreakdown) => ({
  title: ProducingLossesLabelsEnum.qualityLoss,
  name: ProducingLossesEnum.qualityLoss,
  time: data.producing.qualityGainLossTime,
  color: data.producing.qualityGainLossTime < 0 ? darkGreen : speedLoss,
});

export const SLOWDOWN_LOSS = (data: PnlBreakdown) => ({
  title: ProducingLossesLabelsEnum.slowdownLoss,
  name: ProducingLossesEnum.slowdownLoss,
  time: data.producing.slowdownGainLossTime,
  color: data.producing.slowdownGainLossTime < 0 ? darkGreen : speedLoss,
});
export const MINOR_STOP_LOSS = (data: PnlBreakdown) => ({
  title: ProducingLossesLabelsEnum.minorStopLoss,
  name: ProducingLossesEnum.minorStopLoss,
  time: data.unknown?.minorStops?.durationMs ?? 0,
  nbEvents: data.unknown.minorStops.numberOfEvents,
  color:
    (data.unknown?.minorStops?.durationMs ?? 0) < 0 ? darkGreen : speedLoss,
});

export const GENERIC_PRODUCING_PNL_LOSSES = (data: PnlBreakdown) => [
  EFFECTIVE_PRODUCING(data),
  QUALITY_LOSS(data),
  SLOWDOWN_LOSS(data),
  MINOR_STOP_LOSS(data),
];

/* Adds subrows to producing category:
 *  - effective producing time
 *  - quality loss + WIP
 *  - slowdown loss
 *  - minor stop loss
 * @param {DataDumpAggregateType} data
 * @return {*}
 */
export const getPnlTimesWithProducingLosses = ({
  data,
  producingLossesToSkip = [],
}: {
  data: PnlBreakdown;
  producingLossesToSkip?: ProducingLossesEnum[];
}) => {
  const producingPnlLosses = GENERIC_PRODUCING_PNL_LOSSES(data);
  // Remove producing losses to skip
  producingLossesToSkip.forEach(producingLoss => {
    const minorStopIndex = producingPnlLosses.findIndex(
      ({ name }) => name === producingLoss
    );
    producingPnlLosses.splice(minorStopIndex, 1);
  });

  return getPnlTimes({
    data,
    categoryPropsArr: GENERIC_PNL_CATEGORY_PROPS_ARR(producingPnlLosses),
  });
};
